/**
 * Portal base URL mapping per market (country short name).
 *
 * Used to build partner survey entry links in the exact format expected by
 * each country's panel frontend (see legacy reference:
 * C:\Projects\polls\surveywebfrontend\src\Components\Surveys\DashboardDetails.tsx:1190
 * and C:\Projects\polls\backend\src\Controllers\surveys.controllers.js:3527,3570):
 *
 *   {portal_base_url}/#/partner?partnerid={partner_uuid}&surveyid={survey_uuid}&rid={respondent_id}
 *
 * Keep this map as the single source of truth for partner-link generation on
 * the admin panel. If additional markets are added, extend the mapping below.
 */

export const PORTAL_BASE_URL_BY_MARKET: Readonly<Record<string, string>> = Object.freeze({
    IN: 'https://panel.indiapolls.com',
    UK: 'https://panel.ukpolls.com',
    SG: 'https://panel.singapore-polls.com',
    MY: 'https://panel.malaysiapolls.com',
    PH: 'https://panel.philippinespolls.com',
    JP: 'https://panel.nipponpolls.com',
    // TODO(client): no US country portal URL has been provided yet. Until the
    // real `panel.<us-domain>` hostname is supplied, US partner links fall back
    // to DEFAULT_PORTAL_BASE_URL (panel.dataxing.com). Add the entry here when
    // the URL is known — no other code change is required because every link
    // generator (frontend Utils/portalUrls.ts and backend utils/portalUrls.js)
    // already routes through this map.
});

/** Default fallback used when a market short-name is missing from the map. */
export const DEFAULT_PORTAL_BASE_URL = 'https://panel.dataxing.com';

/**
 * Brand-neutral surveys host. The partner-entry endpoint (GET /p) lives here
 * and returns a 302 to the substituted client-survey URL. Distinct from the
 * admin panel host (DEFAULT_PORTAL_BASE_URL) so vendor-facing links never
 * leak the panel domain. Override via REACT_APP_SURVEYS_BASE_URL — local dev
 * usually points this at http://localhost:3001 so the admin frontend dev
 * server can proxy /p through to the backend.
 */
export const SURVEYS_BASE_URL =
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SURVEYS_BASE_URL)
        ? process.env.REACT_APP_SURVEYS_BASE_URL
        : 'https://surveys.dataxing.com';

/**
 * Returns the portal base URL for a given market short code (e.g. "IN", "UK").
 * Falls back to DEFAULT_PORTAL_BASE_URL when the market is unknown.
 */
export function getPortalBaseUrl(marketCode?: string | null): string {
    if (!marketCode) return DEFAULT_PORTAL_BASE_URL;
    const key = String(marketCode).trim().toUpperCase();
    return PORTAL_BASE_URL_BY_MARKET[key] || DEFAULT_PORTAL_BASE_URL;
}

/**
 * Builds a partner survey link in the canonical format. Vendor traffic lands
 * on SURVEYS_BASE_URL (brand-neutral) and the path is `/p` — a backend-only
 * 302 redirect, no React rendering. Works for every market (panel +
 * non-panel) because the substituted client-survey URL is the same
 * regardless of country. The `marketCode` argument is preserved on the
 * signature for back-compat with existing call sites but is intentionally
 * ignored.
 *
 * The `{respondent_id}` token is left as a literal placeholder so partners
 * can substitute their own respondent identifier when redirecting traffic.
 */
export function buildPartnerSurveyLink(params: {
    marketCode?: string | null;
    partnerId: string;
    surveyId: string;
}): string {
    return `${SURVEYS_BASE_URL}/p?partnerid=${params.partnerId}&surveyid=${params.surveyId}&rid={respondent_id}`;
}
